/**
 * QUÉ AYUDA LE TOCA A CADA PANTALLA (2026-10-26).
 *
 * Va aparte de los textos (`temas.ts`) a propósito: el botón «Ayuda» está en
 * todas las pantallas y solo necesita saber SI hay ayuda. Los textos, que son
 * largos, se descargan al abrirla.
 *
 * Pantalla nueva con ayuda = su dirección aquí y su tema en `temas.ts`. La
 * prueba `ayuda.test.ts` falla si una de las dos cosas falta.
 *
 * Las subopciones (`?ver=`, `?tipo=`) comparten la ayuda de su pantalla: es la
 * misma pantalla con otra pestaña abierta.
 */

import { matchPath } from "react-router-dom";

export const RUTAS_DE_AYUDA: Record<string, string> = {
  // Inicio y Mostrador
  "/": "inicio",
  "/vender": "vender",
  "/ventas": "ventas",
  "/ventas/registro": "registro-de-ventas",
  "/ventas-sin-enviar": "ventas-sin-enviar",
  "/cotizaciones": "cotizaciones",
  "/apartados": "apartados",
  "/devoluciones": "devoluciones",
  "/clientes": "clientes",
  "/clientes/importar": "importar-clientes",
  "/salon": "salon",
  "/cocina": "cocina",
  "/reservaciones": "reservaciones",
  "/agenda": "agenda",
  "/sala-de-espera": "sala-de-espera",
  "/pacientes": "pacientes",
  "/pacientes/:pacienteId": "expediente-clinico",
  "/pacientes/:pacienteId/consulta/:notaId": "consulta-medica",
  "/autoservicio": "autoservicio",
  "/turnos": "turnos",
  "/turnos/atender": "atender-turnos",
  "/publicidad": "pantalla-publicitaria",

  // Negocio
  "/catalogo": "catalogo",
  "/catalogo/categorias": "categorias",
  "/catalogo/precios": "precios",
  "/catalogo/codigos": "codigos",
  "/catalogo/opciones": "opciones",
  "/catalogo/importar": "importar-catalogo",
  "/impuestos": "impuestos",
  "/etiquetas": "etiquetas",
  "/produccion": "produccion",
  "/merma": "control-de-merma",
  "/merma/:conteoId": "control-de-merma",
  "/catalogo-web": "catalogo-web",
  "/inventario": "inventario",
  "/inventario/entradas": "entradas",
  "/inventario/cargar-existencias": "cargar-existencias",
  "/inventario/movimientos": "movimientos",
  "/inventario/traslados": "traslados",
  "/inventario/traslados/nuevo": "nuevo-traslado",
  "/inventario/traslados/:id": "traslado",
  "/inventario/traslados/:id/recoger": "recoger",
  "/inventario/almacenes": "almacenes",
  "/inventario/ubicaciones": "ubicaciones",
  "/inventario/ubicaciones/contar": "contar",
  "/inventario/plano": "plano-del-almacen",
  "/inventario/proveedores": "proveedores",

  // Dinero
  "/cuentas": "cuentas",
  "/cuentas/movimientos": "movimientos-de-cuentas",
  "/cuentas/:id": "libro-de-cuenta",
  "/ajustes/metodos-pago": "metodos-de-pago",
  "/ajustes/listas-de-precios": "listas-de-precios",
  "/caja": "caja",
  "/caja/historial": "historial-de-caja",
  "/caja/cajas": "cajas",
  "/gastos": "gastos",
  "/nomina": "nomina",
  "/credito": "credito",
  "/credito/:id": "expediente",
  "/por-pagar": "por-pagar",
  "/por-pagar/beneficiario": "beneficiario",
  "/pagos-por-revisar": "pagos-por-revisar",
  "/monedas": "monedas",

  // Análisis
  "/reportes": "reportes",
  "/reportes/detalle": "reporte-detallado",
  "/reportes/utilidad": "utilidad",
  "/reportes/cuentas": "reporte-de-cuentas",
  "/reportes/estadisticas": "estadisticas",
  "/reportes/estadisticas/ranking": "ranking",
  "/reportes/rotacion": "rotacion",
  "/reportes/prediccion": "prediccion",
  "/reportes/costos": "costos",
  "/reportes/precios": "reporte-de-precios",
  "/sofi": "sofi",

  // Administrar
  "/calendario": "calendario",
  "/equipo": "equipo",
  "/ajustes/sucursales": "sucursales",
  "/ajustes/bot": "bot",
  "/ajustes": "ajustes",
  "/ajustes/negocio": "datos-del-negocio",
  "/ajustes/funciones": "funciones",
  "/ajustes/modo": "modo",
  "/ajustes/restaurante": "ajustes-restaurante",
  "/ajustes/consultorio": "ajustes-consultorio",
  "/ajustes/impresora": "impresora",
  "/ajustes/balanza": "balanza",
  "/ajustes/cuenta": "tu-cuenta",
  "/ajustes/sofi": "ajustes-sofi",
  "/ajustes/sugerencias": "sugerencias",
  "/ajustes/pasarela": "pasarela",

  // Todas las sucursales
  "/sucursales": "todas-resumen",
  "/sucursales/ventas": "todas-ventas",
  "/sucursales/clientes": "todas-clientes",
  "/sucursales/dinero": "todas-dinero",
  "/sucursales/por-cobrar": "todas-por-cobrar",
  "/sucursales/por-pagar": "todas-por-pagar",
  "/sucursales/inventario": "todas-inventario",
  "/sucursales/consultor": "todas-consultor",
  "/sucursales/rotacion": "todas-rotacion",
  "/sucursales/prediccion": "todas-prediccion",
  "/sucursales/calendario": "todas-calendario",

  // Todos mis negocios: las mismas pantallas, contando negocios (ver `enNegocios`)
  "/grupo": "grupo-resumen",
  "/grupo/negocios": "grupo-negocios",
  "/grupo/ventas": "todas-ventas",
  "/grupo/clientes": "todas-clientes",
  "/grupo/dinero": "todas-dinero",
  "/grupo/por-cobrar": "todas-por-cobrar",
  "/grupo/por-pagar": "todas-por-pagar",
  "/grupo/inventario": "todas-inventario",
  "/grupo/calendario": "todas-calendario",
};

/** Las direcciones con un trozo variable (`:id`), que no se encuentran tal cual. */
const CON_VARIABLE = Object.keys(RUTAS_DE_AYUDA).filter((r) => r.includes(":"));

/** El tema de ayuda de una dirección, o `null` si esa pantalla no tiene. */
export function temaDeLaRuta(direccion: string): string | null {
  // «/catalogo/» y «/catalogo» son la misma pantalla.
  const limpia = direccion.length > 1 ? direccion.replace(/\/+$/, "") : direccion;
  const exacta = RUTAS_DE_AYUDA[limpia];
  if (exacta) return exacta;
  const patron = CON_VARIABLE.find((p) => matchPath(p, limpia));
  return patron ? (RUTAS_DE_AYUDA[patron] ?? null) : null;
}

/**
 * ¿Es una pantalla de «Todos mis negocios»? Ahí las pantallas de «Todas las
 * sucursales» cuentan negocios, y su ayuda tiene que decir lo mismo.
 */
export function enNegocios(direccion: string): boolean {
  return direccion === "/grupo" || direccion.startsWith("/grupo/");
}
